import { apiGet, apiGetMeta, apiPost } from './api-client.js';

/**
 * Typed wrappers over API_CONTRACT.md §13.7's four notification endpoints
 * (`notification-routes.ts`, read directly off the running server's transport code —
 * there is no `VERIFIED_RESPONSES.md` entry for this module yet, so the shapes below are
 * built from the route handler's explicit field list rather than transcribed prose).
 *
 * **Ownership is not a parameter here; it is the shape of the API** (the same point the
 * server's own file header makes): `GET /notifications`, `GET /notifications/unread-count`
 * and `POST /notifications/read-all` take no recipient of any kind — the caller's own feed
 * is the only thing they can ask for. `POST /notifications/:id/read` is the one endpoint
 * that names a record, and a record addressed to someone else answers `403
 * FORBIDDEN_OWNERSHIP` (AC-34) — rendered distinctly from a `404` (out of tenant, or does
 * not exist at all) rather than collapsed into one "couldn't mark as read" message.
 */

export interface NotificationRow {
  id: string;
  templateCode: string;
  locale: 'ne' | 'en';
  params: Record<string, unknown>;
  /** Server-rendered, already in the recipient's stored locale (API_CONTRACT.md §11) — never re-translated client-side. */
  body: string;
  subjectType: string | null;
  subjectId: string | null;
  subjectOccurredAt: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface CursorPageMeta {
  nextCursor: string | null;
  hasMore: boolean;
}

export async function listNotifications(
  params: { unread?: boolean; cursor?: string; limit?: number } = {},
  signal?: AbortSignal,
): Promise<{ items: NotificationRow[]; page: CursorPageMeta }> {
  const usp = new URLSearchParams();
  if (params.unread !== undefined) usp.set('unread', String(params.unread));
  if (params.cursor !== undefined) usp.set('cursor', params.cursor);
  if (params.limit !== undefined) usp.set('limit', String(params.limit));
  const qs = usp.toString();
  const { data, meta } = await apiGetMeta<NotificationRow[]>(`/notifications${qs ? `?${qs}` : ''}`, signal);
  const page = (meta?.page as CursorPageMeta | undefined) ?? { nextCursor: null, hasMore: false };
  return { items: data, page };
}

/**
 * `GET /notifications/unread-count` — "on failure the client keeps the LAST KNOWN count
 * rather than showing zero" (§13.7, I11's own spec) — that fallback is the caller's job
 * (react-query's cached previous value does this for free, see `AppShell`), not this
 * function's; this only reports what the server actually said.
 */
export function unreadNotificationCount(signal?: AbortSignal): Promise<{ unread: number }> {
  return apiGet<{ unread: number }>('/notifications/unread-count', signal);
}

export function markNotificationRead(notificationId: string, signal?: AbortSignal): Promise<{ id: string; read: boolean; changed: boolean }> {
  return apiPost<{ id: string; read: boolean; changed: boolean }>(`/notifications/${notificationId}/read`, undefined, signal);
}

export function markAllNotificationsRead(signal?: AbortSignal): Promise<{ marked: number }> {
  return apiPost<{ marked: number }>('/notifications/read-all', undefined, signal);
}

export { ApiError, NetworkError } from './api-client.js';
